import React, { createContext, useContext, useState, useRef } from 'react';
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast";

const AttachmentContext = createContext();

export const useAttachments = () => {
    const context = useContext(AttachmentContext);
    if (!context) {
        throw new Error('useAttachments must be used within AttachmentProvider');
    }
    return context;
};

export const AttachmentProvider = ({ children, task, onTaskChange, descriptionEditorRef, newTask = false }) => {
    const { axiosInstance } = useAuth();
    const { toast } = useToast();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [attachmentToDelete, setAttachmentToDelete] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [submissionFileUrls, setSubmissionFileUrls] = useState({});
    const fileInputRef = useRef(null);

    // Generate URLs for submission files so thumbnails can display
    React.useEffect(() => {
        if (newTask && task?.attachments) {
            const generateUrls = async () => {
                const urls = {};
                for (const file of task.attachments) {
                    if (file.fromSubmission && file.key && file.contentType?.startsWith('image/')) {
                        try {
                            const response = await axiosInstance.get(`/forms/files/${file.key}`);
                            urls[file.key] = response.data.url;
                        } catch (error) {
                            console.error('Error getting file URL:', error);
                        }
                    }
                }
                setSubmissionFileUrls(urls);
            };

            generateUrls();
        }
    }, [task?.attachments, newTask, axiosInstance]);

    const handleFileUpload = async (files) => {
        if (!files || files.length === 0) return;

        console.log('Starting upload of files:', files.map(f => ({
            name: f.name,
            size: f.size,
            type: f.type
        })));

        // For newTask mode, just add the files to the task without uploading
        if (newTask) {
            const tempFiles = files.map((file, index) => {
                const uploadId = Math.random().toString(36).substring(7);
                return {
                    id: uploadId,
                    key: `temp_${Date.now()}_${index}_${Math.random().toString(36).substring(7)}`,
                    name: file.name,
                    contentType: file.type,
                    type: file.type,
                    size: file.size,
                    url: URL.createObjectURL(file),
                    file: file,
                    alt: file.name,
                    uploadId: uploadId,
                    fromSubmission: false
                };
            });
            
            const updatedTask = {
                ...task,
                attachments: [...(task.attachments || []), ...tempFiles]
            };
            
            onTaskChange(updatedTask);
            return;
        }

        // For existing tasks, upload all files
        setIsUploading(true);
        setUploadProgress(0);

        try {
            // Upload files sequentially to avoid overwhelming the server
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const formData = new FormData();
                formData.append('file', file);

                const response = await axiosInstance.post(
                    `/tasks/${task.organization.slug}/${task.key}/attachments`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                        onUploadProgress: (progressEvent) => {
                            const fileProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            const totalProgress = Math.round(((i * 100) + fileProgress) / files.length);
                            setUploadProgress(totalProgress);
                        },
                        timeout: 30000
                    }
                );
                
                // Update task with the latest response (includes all attachments)
                onTaskChange(response.data);
            }

            toast({
                description: `${files.length} attachment${files.length > 1 ? 's' : ''} uploaded successfully`,
            });
        } catch (error) {
            console.error('Upload error:', error);
            toast({
                description: error?.response?.data?.message || "Failed to upload files",
                variant: "destructive"
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDeleteAttachment = async (attachment) => {
        if (newTask) {
            // For new tasks, just remove from the array
            const updatedAttachments = task.attachments.filter(att => att !== attachment);
            onTaskChange({ ...task, attachments: updatedAttachments });
            setAttachmentToDelete(null);
            setDeleteModalOpen(false);
            return;
        }

        // For existing tasks, call the API
        setIsDeleting(true);
        try {
            const response = await axiosInstance.delete(
                `/tasks/${task.organization.slug}/${task.key}/attachments`,
                { data: { key: attachment.key } }
            );

            if (descriptionEditorRef?.current) {
                descriptionEditorRef.current.commands.setContent(response.data.description);
            }

            onTaskChange(response.data);
            setDeleteModalOpen(false);
            setAttachmentToDelete(null);
            toast({
                description: "Attachment deleted successfully"
            });
        } catch (error) {
            console.error('Error deleting attachment:', error);
            toast({
                description: error?.response?.data?.message || "Failed to delete attachment",
                variant: "destructive"
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleOpenAttachment = (url) => {
        window.open(url, '_blank');
    };

    const triggerFileUpload = () => {
        fileInputRef.current?.click();
    };

    const value = {
        // State
        task,
        newTask,
        isUploading,
        uploadProgress,
        attachmentToDelete,
        deleteModalOpen,
        isDeleting,
        submissionFileUrls,
        fileInputRef,
        
        // Actions
        handleFileUpload,
        handleDeleteAttachment,
        handleOpenAttachment,
        triggerFileUpload,
        setAttachmentToDelete,
        setDeleteModalOpen,
        
        // Helpers
        getAttachmentUrl: (attachment) => {
            if (attachment.fromSubmission && submissionFileUrls[attachment.key]) {
                return submissionFileUrls[attachment.key];
            }
            return attachment.url;
        }
    };

    return (
        <AttachmentContext.Provider value={value}>
            {children}
        </AttachmentContext.Provider>
    );
}; 